import {
    Box,
    Button,
    CloseButton,
    Flex,
    Icon,
    Portal,
    Text,
    useColorMode,
    useColorModeValue,
    useToast
} from '@chakra-ui/react';
import { useRef } from 'react';

interface ToastProps {
    title: string;
    type: string;
    subtitle: string;
    resClick?: () => void;
}

export default function useCustomToast() {
    const toast = useToast();
    const toastIdRef = useRef(undefined);

    function close() {
        if (toastIdRef.current) {
            toast.close(toastIdRef.current);
        }
    }

    const showToast = ({
        title,
        type,
        subtitle,
        resClick = null
    }: ToastProps) => {
        toastIdRef.current = toast({
            position: 'bottom-right',
            isClosable: true,
            duration: 3800,
            render: () => (
                <ToastPopper
                    onClose={close}
                    title={title}
                    type={type}
                    subtitle={subtitle}
                    resClick={resClick}
                />
            )
        });
    };

    return showToast;
}

const ToastPopper = ({
    onClose,
    title,
    type,
    subtitle,
    resClick,
    infoSecondRow = 'button'
}) => {
    //Colorstuff
    //const { colorMode, toggleColorMode } = useColorMode();
    const colorMode = useColorMode().colorMode;
    const textColor = useColorModeValue('gray.600', '#FFF');
    const toastBg = useColorModeValue('#FFF', '#262A2D');
    const titleColor = useColorModeValue('#1A202C', '#FFF');
    const subText = useColorModeValue('gray.500', 'gray.400');
    const iconSmall = useColorModeValue('gray.700', 'gray.400');
    const buttonBorder = useColorModeValue('gray.300', 'gray.500');
    const buttonHover = useColorModeValue('gray.100', 'gray.700');
    const buttonActive = useColorModeValue('gray.200', '#262A2D');
    const toastShadow = useColorModeValue(
        '0px 0px 1px rgb(172 174 179 / 52%), 0px 10px 12px -2px rgb(108 109 115 / 24%), 0px 24px 54px 3px rgba(108,109,115,0.15)', //'0 12px 32px -12px rgba(152,169,185,0.85), 0 0px 10px -4px rgba(152,169,185,0.55)',
        '0px 0px 1px rgba(19,20,24,0.95), 0px 10px 12px -2px rgba(19,20,24,0.74), 0px 24px 54px 3px rgba(19,20,24,0.65)'
    );

    const toastConfig = {
        restore: {
            icon: WarningIcon,
            color: 'rgb(255, 152, 22, 0.3)',
            boxBg: 'rgb(255, 152, 22, 0.2)',
            shadow: '#EB8C14',
            iconColor: '#FF9816',
            iconSize: '24px',
            button: true,
            buttonText: 'Restore'
        },
        warning: {
            icon: WarningIcon,
            color: 'rgb(255, 152, 22, 0.3)',
            boxBg: 'rgb(255, 152, 22, 0.2)',
            shadow: '#EB8C14',
            iconColor: '#FF9816',
            iconSize: '20px'
        },
        success: {
            icon: CheckIcon,
            color: 'rgb(59, 222, 134, 0.3)',
            boxBg: 'rgb(59, 222, 134, 0.2)',
            shadow: '#31B66E',
            iconColor: '#3BDE86',
            iconSize: '24px'
        },
        error: {
            icon: ErrorIcon,
            color: 'rgb(245, 101, 101, 0.3)',
            boxBg: 'rgb(245, 101, 101, 0.2)',
            shadow: '#CC5252',
            iconColor: '#F56565',
            iconSize: '24px'
        },
        info: {
            icon: InfoIcon,
            color: 'rgba(51,145,255,0.30)',
            boxBg: 'rgba(51,145,255,0.20)',
            shadow: '#2878D5',
            iconColor: '#3391FF',
            iconSize: '20px'
        },
        infoButton: {
            icon: InfoIcon,
            color: 'rgba(51,145,255,0.30)',
            boxBg: 'rgba(51,145,255,0.20)',
            shadow: '#2878D5',
            iconColor: '#3391FF',
            iconSize: '20px',
            button: true,
            buttonText: 'Turn note into site'
        },
        reminder: {
            icon: AlarmIcon,
            color: 'rgba(51,145,255,0.30)',
            boxBg: 'rgba(51,145,255,0.20)',
            shadow: '#2878D5',
            iconColor: '#3391FF',
            iconSize: '18px'
        }
    };

    const config = toastConfig[type];

    return (
        <>
            <Box
                minHeight="40px"
                bg={toastBg}
                minWidth="312px"
                rounded="12px"
                boxShadow={toastShadow}
                mr="8px"
                mb="8px"
                pb="12px"
                zIndex={9999999999}
                //m={4}
            >
                <Flex
                    w="100%"
                    onClick={() => {
                        onClose();
                    }}
                >
                    <CloseButton
                        cursor="pointer"
                        bg="transparent"
                        name="close"
                        color="gray.500"
                        size={'sm'}
                        boxSize={'10px'}
                        ml="auto"
                        mt="16px"
                        mr="16px"
                    />
                </Flex>
                <Flex alignItems="top">
                    <Flex
                        bg={config.color}
                        w="4px"
                        h="36px"
                        alignItems="center"
                        roundedRight="6px"
                        mr="10px"
                    ></Flex>
                    <Flex
                        mt="2px"
                        h="32px"
                        w="32px"
                        rounded="11px"
                        alignItems={'center'}
                        bg={config.boxBg}
                        boxShadow={
                            '0 0 12px 1px ' +
                            (colorMode === 'light'
                                ? config.iconColor + '36'
                                : config.shadow + '38')
                        }
                    >
                        {/* <motion.div
                            initial={{ scale: 1.375 }}
                            animate={{ scale: 1 }}
                            transition={{
                                delay: 0.1,
                                duration: 0.4,
                                type: 'spring',
                                stiffness: 120,
                                damping: 10,
                                mass: 1.25
                            }}
                            style={{
                                background: config.boxBg,
                                borderRadius: '11px',
                                top: 0,
                                left: 0,
                                height: '32px',
                                width: '32px',
                                zIndex: 2,
                                position: 'absolute'
                            }}
                        /> */}
                        <Box m="auto">
                            <Icon
                                /* ml="22px"
                        mr="8px" */
                                as={config.icon}
                                color={config.iconColor}
                                boxSize={config.iconSize}
                                m="auto"
                            />
                        </Box>
                    </Flex>
                    <Box
                        maxWidth="264px"
                        /* pl="44px" mr="28px" */
                        minWidth="248px"
                        mx="12px"
                        pb="16px"
                    >
                        <Text
                            fontSize="16px"
                            fontWeight="500"
                            ml="0px"
                            mr="40px"
                            color={titleColor}
                            mb="8px"
                            mt="6px"
                        >
                            {title}
                        </Text>
                        {config.button && resClick !== null ? (
                            <Button
                                onClick={() => {
                                    resClick();
                                    onClose();
                                }}
                                mt="4px"
                                w="calc(100% - 12px)"
                                variant="outline"
                                borderColor={buttonBorder}
                                _hover={{ bg: buttonHover }}
                                _active={{ bg: buttonActive }}
                                size="sm"
                                color={titleColor}
                            >
                                <Icon as={RestoreIcon} color={iconSmall} />
                                {config.buttonText}
                            </Button>
                        ) : (
                            <Text
                                color={subText}
                                fontSize="15px"
                                fontWeight="400"
                                mt="-4px"
                                minH="12px"
                            >
                                {subtitle || ''}
                            </Text>
                        )}
                    </Box>
                </Flex>
            </Box>
        </>
    );
};

const AlarmIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{
                width: '18px',
                height: '18px'
            }}
        >
            <path
                d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
                fill={'#3391FF'}
            />
        </svg>
    );
};

const WarningIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="#FF9816"
            style={{ width: '24px', height: '24px' }}
        >
            <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const InfoIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="#3391FF"
            style={{ width: '24px', height: '24px' }}
        >
            <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
            />
        </svg>
    );
};
const ErrorIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="#F56565"
            style={{ width: '24px', height: '24px' }}
        >
            <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const RestoreIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-chakra-subtle-text)"
            style={{ width: '14px', height: '14px', marginRight: '8px' }}
        >
            <path
                fillRule="evenodd"
                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const CheckIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="#3BDE86"
            style={{ width: '24px', height: '24px' }}
        >
            <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
            />
        </svg>
    );
};
